import React from "react";
import PolicyLayout from "../Policy/PolicyLayout";

const PrivacyForm = () => {
  const sections = [
    {
      id: "info-collected",
      title: "Information We Collect",
      body: (
        <>
          <p>We may collect and process the following information about you:</p>
          <p>
            <b>Information you provide:</b> data entered when registering
            (name, email, contact details, password), details provided during
            competitions, promotions, surveys, reviews, testimonials or
            feedback, and data shared when contacting our support team.
          </p>
          <p>
            <b>Transaction and payment information:</b> records of transactions
            including order details, payment methods and delivery preferences.
            All credit/debit card details and personally identifiable
            information will <b>not</b> be stored, sold, shared, rented or
            leased to any third parties.
          </p>
          <p>
            <b>Device and technical information:</b> data about your device
            such as IP address, operating system, browser type and device
            identifiers, plus information gathered through cookies, tracking
            pixels and similar technologies.
          </p>
          <p>
            <b>Location data:</b> details of your location when using
            location-based features, provided you have enabled this on your
            device.
          </p>
          <p>
            <b>Third-party data:</b> information obtained from third-party
            services with your consent, such as email addresses shared via
            social platforms.
          </p>
        </>
      ),
    },
    {
      id: "info-use",
      title: "How We Use Information",
      body: (
        <ul>
          <li>Deliver, maintain and improve our services and your experience</li>
          <li>Fulfil your orders and provide requested services</li>
          <li>Notify you about updates, promotions, or changes to our services</li>
          <li>Enhance website content and functionality for optimal interaction</li>
          <li>Administer promotional campaigns, incentives and sweepstakes</li>
          <li>Detect and prevent fraudulent activities and security threats</li>
        </ul>
      ),
    },
    {
      id: "cookies",
      title: "Cookies & Tracking Technologies",
      body:
        "We use cookies and similar technologies to personalize content and improve user experience, track website usage, analyze trends, and provide targeted recommendations. You can manage cookie preferences through your browser settings — disabling cookies may limit certain functionalities of our platform.",
    },
    {
      id: "retention",
      title: "Data Retention & Deletion",
      body: (
        <>
          <p>
            We retain your data only for as long as necessary to provide and
            improve our services, comply with legal/tax/audit obligations, and
            resolve disputes. If you close your account, we will delete or
            anonymize your data unless legally required to retain it.
          </p>
          <p>Examples of retention include:</p>
          <ul>
            <li>Unresolved disputes or claims</li>
            <li>Compliance with tax or legal obligations</li>
            <li>Fraud prevention and security measures</li>
          </ul>
        </>
      ),
    },
    {
      id: "rights",
      title: "Your Rights",
      body: (
        <ul>
          <li><b>Access</b> the personal data we hold about you</li>
          <li><b>Rectify</b> inaccurate or outdated information</li>
          <li><b>Delete</b> your personal data, subject to legal obligations</li>
          <li><b>Restrict</b> how your data is processed in certain situations</li>
          <li><b>Portability</b> — receive a copy of your data in a structured format</li>
          <li><b>Opt out</b> of marketing communications at any time</li>
        </ul>
      ),
    },
    {
      id: "third-party",
      title: "Third-Party Services",
      body: (
        <>
          <p>We work with trusted third-party partners to enhance our platform:</p>
          <ul>
            <li><b>Payment processors</b> for secure transaction handling</li>
            <li><b>Analytics providers</b> to analyze user behavior and improve services</li>
            <li><b>Cloud hosting</b> ensuring availability and redundancy</li>
          </ul>
          <p>
            These third parties are contractually obligated to process your
            data in compliance with strict confidentiality and data protection
            standards. They may not sell, disclose, or use your information for
            purposes beyond the scope of our instructions.
          </p>
        </>
      ),
    },
    {
      id: "security",
      title: "Data Security Measures",
      body: (
        <>
          <p>
            We prioritize safeguarding your personal information with robust
            technical and organizational measures, including:
          </p>
          <ul>
            <li><b>Encryption:</b> all sensitive information is secured with industry-standard SSL</li>
            <li><b>Access control:</b> restricted to authorized personnel for operational purposes</li>
            <li><b>Regular audits:</b> periodic reviews ensure systems meet the highest security standards</li>
            <li><b>Incident response:</b> a dedicated team to mitigate impact and notify users promptly if a breach occurs</li>
          </ul>
          <p>
            Despite our efforts, no method of transmission or storage is
            entirely secure. We recommend safeguarding your account credentials
            and using secure networks.
          </p>
        </>
      ),
    },
    {
      id: "children",
      title: "Children's Privacy",
      body: (
        <p>
          Our services are not directed to individuals under the age of 13. We
          do not knowingly collect personal information from children. If we
          identify that a minor has provided personal data, we will take
          immediate action to delete it. Parents or guardians who suspect their
          child has interacted with our platform should contact us at{" "}
          <a href="mailto:support@globalgiftpass.io">
            support@globalgiftpass.io
          </a>
          .
        </p>
      ),
    },
    {
      id: "delivery",
      title: "Delivery & Shipping",
      body:
        "GlobalGiftPass.io will not deal with or provide products or services to OFAC (Office of Foreign Assets Control) sanctioned countries in compliance with UAE law. Digital products such as gift cards are delivered electronically to the email address provided during checkout.",
    },
    {
      id: "law",
      title: "Governing Law & Jurisdiction",
      body:
        "All matters related to the use of GlobalGiftPass.io, including disputes or claims arising from its use, are governed by and construed in accordance with the laws of the United Arab Emirates (UAE). Any legal proceedings must be initiated within the UAE, and disputes will be resolved under the jurisdiction of UAE courts.",
    },
    {
      id: "changes",
      title: "Changes to This Policy",
      body:
        "We reserve the right to modify this Privacy Policy to reflect changes in our practices or evolving regulatory requirements. Significant updates will be communicated through notifications on the platform and direct email when applicable. Continued use of the platform constitutes acknowledgment of these updates.",
    },
    {
      id: "contact",
      title: "Contact Information",
      body: (
        <>
          <p>
            If you have questions or concerns regarding this Privacy Policy or
            wish to exercise your rights, you can reach us at:
          </p>
          <ul>
            <li>
              Email:{" "}
              <a href="mailto:support@globalgiftpass.io">support@globalgiftpass.io</a>
            </li>
            <li>VentureWorks Dynamics LLC, Sharjah Media City, Sharjah, UAE</li>
          </ul>
        </>
      ),
    },
  ];

  return (
    <PolicyLayout
      slug="privacy"
      eyebrow="PRIVACY POLICY"
      title="How we protect"
      accent="your data"
      sub="GlobalGiftPass.io is operated by Ventureworks Dynamics LLC. This policy explains what we collect, how we use it, and the rights you have."
      lastUpdated="January 2026"
      intro={
        <>
          <p>
            Welcome to <b>GlobalGiftPass.io</b>, a platform owned and operated
            by Ventureworks Dynamics LLC. This Privacy Policy outlines how we
            collect, use, and protect your personal information when you access
            or use our website and services.
          </p>
          <p>
            By using our services, you agree to the terms of this policy, which
            is designed to ensure transparency and protect your privacy while
            complying with applicable laws and standards.
          </p>
        </>
      }
      notice="We will never store, sell, share, rent or lease your credit/debit card details or personally identifiable information to any third party."
      sections={sections}
    />
  );
};

export default PrivacyForm;
